import { createContext, useContext } from 'react';

/*
 * Tag scoping: which products a trained answer (or an auto-response rule) applies to.
 *
 *   all products (`all_tours`), or the picked products ∪ products carrying any picked System tag
 *   (`collections`, of any category) ∪ products carrying any picked Access tag (`access_tags`),
 *   and from that, minus `excluded_tours` -- "All products except …" included.
 *
 * A tag covers products added to it later, which is why tags are saved rather than expanded into
 * products, and why leaving one product out is an exclusion rather than a shorter product list.
 * Backend: demo/lib/ai_tag_scope.py; plan cplanetbackend/aidocs/plans/2026-10-16-ai-suite-tag-scoping.md.
 *
 * Featured categories (operator config ai_featured_tag_categories, read by the backend; Crown has
 * Collection and Destination) are offered first, each under its own name -- what "Collections"
 * (CP-8473) did for one category. Every other System tag and every Access tag on at least one
 * product is reachable through "+ Other tag", for every operator.
 *
 * The tab builds one value from the products/ response and provides it:
 *   { featured: [{ code, name, tags }], otherTags, accessTags, tagsOf: { tourId: [ids] }, atagsOf }
 * Each featured tag carries its `category`, as a saved answer's tags do.
 */
export const EMPTY_SCOPE = { featured: [], otherTags: [], accessTags: [], tagsOf: {}, atagsOf: {} };

export const TagScopeContext = createContext(null);

// Never null: before the products load (or outside the inbox) there are no tags to offer.
export const useTagScope = () => useContext(TagScopeContext) || EMPTY_SCOPE;

const byName = (a, b) => a.name.localeCompare(b.name);

// catalog: the products/ response.
export function buildTagScope(catalog) {
  if (!catalog) return EMPTY_SCOPE;
  // Backends before tag scoping send the one collection category as collection_category +
  // collections, and each product's collection_ids.
  const featured = Array.isArray(catalog.featured)
    ? catalog.featured
    : [{ code: 'collection', name: 'Collection', ...catalog.collection_category, tags: catalog.collections || [] }];
  const tagsOf = {};
  const atagsOf = {};
  (catalog.results || []).forEach((p) => {
    tagsOf[p.id] = p.tag_ids || p.collection_ids || [];
    atagsOf[p.id] = p.atag_ids || [];
  });
  return {
    featured: featured.filter((f) => f.tags?.length).map(({ code, name, tags }) => ({
      code,
      name,
      tags: tags.map((t) => ({ ...t, category: { code, name } })),
    })),
    // Grouped by category in the pickers, so kept in category order.
    otherTags: [...(catalog.other_tags || [])].sort((a, b) => a.category.name.localeCompare(b.category.name) || byName(a, b)),
    accessTags: [...(catalog.access_tags || [])].sort(byName),
    tagsOf,
    atagsOf,
  };
}

// "destination" -> "destinations", "city" -> "cities": picker group headings and search boxes.
export const plural = (word) => {
  if (/[^aeiou]y$/i.test(word)) return `${word.slice(0, -1)}ies`;
  if (/(s|x|z|ch|sh)$/i.test(word)) return `${word}es`;
  return `${word}s`;
};

// The featured category a System tag is in, or null. Tags saved before they carried their
// category were all in the one collection category: matched by id.
export const featuredOf = (ctx, tag) => ctx.featured.find((f) => (tag.category
  ? f.code === tag.category.code
  : f.tags.some((t) => t.id === tag.id))) || null;

/*
 * The pill and chip label of a tag. `kind`: 'tag' (System tag) or 'atag' (Access tag).
 *   featured category   "Granada destination" (as "Colosseum collection" before)
 *   any other category  "Theme: Evening"
 *   Access tag          "Access tag: Naples office"
 */
export function tagLabel(tag, ctx = EMPTY_SCOPE, kind = 'tag') {
  if (kind === 'atag') return `Access tag: ${tag.name}`;
  const f = featuredOf(ctx || EMPTY_SCOPE, tag);
  if (f) return `${tag.name} ${f.name.toLowerCase()}`;
  if (tag.category) return `${tag.category.name}: ${tag.name}`;
  return `${tag.name} collection`;
}

/*
 * Products -> the same products expressed with the FIRST featured category: every product with a
 * tag in it is covered by that tag; products without one stay as products. Without featured
 * categories the products are returned unchanged.
 */
export function scopeFromTours(tours = [], ctx = null) {
  const first = ctx?.featured?.[0];
  if (!first) return { tours, collections: [] };
  const inFirst = new Set(first.tags.map((t) => t.id));
  const ids = new Set();
  const loose = [];
  tours.forEach((t) => {
    const of = (ctx.tagsOf[t.id] || []).filter((id) => inFirst.has(id));
    if (of.length) of.forEach((id) => ids.add(id));
    else loose.push(t);
  });
  return { tours: loose, collections: first.tags.filter((c) => ids.has(c.id)) };
}

// The products (of `products`) a tag covers. `kind`: 'tag' or 'atag'.
export const productsWithTag = (products, ctx, kind, id) => {
  const of = kind === 'atag' ? ctx.atagsOf : ctx.tagsOf;
  return products.filter((p) => (of[p.id] || []).includes(id));
};

/*
 * An exclusion only means something inside a picked tag: drop the ones no remaining tag covers
 * (they would silently come back if the tag were picked again). A product the catalog doesn't know
 * -- not loaded yet, or no longer a recurring product -- is kept.
 */
export function pruneExcluded(excluded, ctx, tags, atags) {
  const tagIds = new Set(tags.map((t) => t.id));
  const atagIds = new Set(atags.map((a) => a.id));
  return excluded.filter((p) => !(p.id in ctx.tagsOf)
    || ctx.tagsOf[p.id].some((id) => tagIds.has(id))
    || (ctx.atagsOf[p.id] || []).some((id) => atagIds.has(id)));
}

const nameList = (list, noun) => (list.length <= 2 ? list.map((t) => t.name).join(', ') : `${list.length} ${noun}`);

/*
 * "Applies to" of a saved answer or rule as chip labels, [{ key, label, title? }]:
 *   [Rome destination] [Access tag: Rome office] [Pompeii Day Trip] [Except Trastevere Food Tour]
 *   [All products except Amalfi Coast Boat Day]   (one entry, key 'all')
 * `scope`: { all_tours, tours, collections, access_tags, excluded_tours }. A rule has no all_tours
 * flag -- nothing picked is every tour -- so it passes `emptyIsAll` (and its own words).
 */
export function scopeChips(scope, ctx = EMPTY_SCOPE, { all = 'All products', noun = 'products', emptyIsAll = false } = {}) {
  const excluded = scope.excluded_tours || [];
  const title = excluded.length ? `Except ${excluded.map((t) => t.name).join(', ')}` : undefined;
  const picked = scope.all_tours ? [] : [
    ...(scope.collections || []).map((c) => ({ key: `c${c.id}`, label: tagLabel(c, ctx) })),
    ...(scope.access_tags || []).map((a) => ({ key: `a${a.id}`, label: tagLabel(a, ctx, 'atag') })),
    ...(scope.tours || []).map((t) => ({ key: `t${t.id}`, label: t.name })),
  ];
  if (scope.all_tours || (emptyIsAll && !picked.length)) {
    return [{ key: 'all', label: excluded.length ? `${all} except ${nameList(excluded, noun)}` : all, title }];
  }
  if (!picked.length || !excluded.length) return picked;
  return [...picked, { key: 'except', label: `Except ${nameList(excluded, noun)}`, title }];
}
